import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, Globe, Shield, 
  Clock, Edit, Calendar, CheckCircle, 
  XCircle, MapPin, DollarSign, Users
} from 'lucide-react';
import { database, ref, get } from '../../firebase';

const Dialog = ({ isOpen, onClose, title, message, type = 'info' }) => {
  if (!isOpen) return null;

  const bgColor = type === 'error' ? 'bg-red-500/10' : 'bg-blue-500/10';
  const borderColor = type === 'error' ? 'border-red-500/30' : 'border-blue-500/30';
  const Icon = type === 'error' ? XCircle : CheckCircle;
  const iconColor = type === 'error' ? 'text-red-400' : 'text-blue-400';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-card border ${borderColor} rounded-xl p-6 max-w-sm w-full`}>
        <div className="text-center">
          <div className={`inline-flex p-3 rounded-full ${bgColor} mb-4`}>
            <Icon className={`w-8 h-8 ${iconColor}`} />
          </div>
          <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
          <p className="text-muted-foreground mb-6">{message}</p>
          <button
            onClick={onClose}
            className="btn-electric px-6 py-2 rounded-lg w-full text-sm"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const dbKey = localStorage.getItem('dbKey');
        const sponsorId = localStorage.getItem('userId');
        const registrationDate = localStorage.getItem('registrationDate');
        
        if (!dbKey || !sponsorId) {
          setDialog({
            isOpen: true,
            title: 'Session Expired',
            message: 'Please login again',
            type: 'error'
          });
          setTimeout(() => navigate('/login'), 1500);
          return;
        }

        let userRef;
        if (registrationDate) {
          userRef = ref(database, `users/${registrationDate}/${dbKey}`);
        } else {
          userRef = ref(database, dbKey);
        }

        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
          setUserData(snapshot.val());
        } else {
          setDialog({
            isOpen: true,
            title: 'Profile Not Found',
            message: 'User data not available',
            type: 'error'
          });
        }
      } catch (error) {
        console.error('Error:', error);
        setDialog({
          isOpen: true,
          title: 'Error',
          message: 'Failed to load profile',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 pt-20 pb-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
            <p className="mt-3 text-sm text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Dialog
        isOpen={dialog.isOpen}
        onClose={() => setDialog({ ...dialog, isOpen: false })}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <div className="max-w-4xl mx-auto px-4 pt-16 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">My Profile</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Account details</p>
          </div>
          <button
            onClick={() => navigate('/user-dashboard/profile/edit')}
            className="btn-electric py-1.5 px-4 rounded-lg flex items-center gap-1.5 text-sm"
          >
            <Edit size={16} />
            Edit
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="bg-card p-3.5 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Investment</div>
            <div className="text-lg font-semibold text-foreground">
              ${userData?.totalInvestment || '0.00'}
            </div>
          </div>
          <div className="bg-card p-3.5 rounded-lg">
            <div className="text-xs text-muted-foreground mb-1">Referrals</div>
            <div className="text-lg font-semibold text-foreground">
              {userData?.referralCount || '0'}
            </div>
          </div>
        </div>

        {/* Personal Info and Account Details in one row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Personal Info */}
          <div className="bg-card p-4 rounded-xl">
            <div className="flex items-center gap-1.5 mb-3">
              <User size={16} className="text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Personal Information</h2>
            </div>
            
            <div className="space-y-3">
              <InfoRow label="Full Name" value={userData?.name} />
              <InfoRow label="Email" value={userData?.email} icon={Mail} />
              <InfoRow label="Mobile" value={userData?.mobile} icon={Phone} />
              <InfoRow label="Country" value={userData?.country} icon={Globe} />
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-card p-4 rounded-xl">
            <div className="flex items-center gap-1.5 mb-3">
              <Shield size={16} className="text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Account Details</h2>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Sponsor ID" value={userData?.sponsorId} />
                <InfoRow label="Upline ID" value={userData?.uplineSponsorId || 'AURUM001'} />
              </div>
              
              <div>
                <div className="text-xs text-muted-foreground mb-1">Status</div>
                <div className={`flex items-center gap-1.5 ${userData?.status === 'active' ? 'text-green-400' : 'text-red-400'}`}>
                  {userData?.status === 'active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                  <span className="text-sm font-medium capitalize">{userData?.status || 'inactive'}</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Registered" value={formatDate(userData?.registrationDate)} icon={Calendar} />
                {/* <InfoRow label="Last Login" value={formatDate(userData?.lastLogin)} icon={Clock} /> */}
              </div>
            </div>
          </div>
        </div>

        {/* Last Login Info - kept separate at bottom if needed */}
        {/* {userData?.lastLoginLocation && (
          <div className="mt-4 bg-card p-4 rounded-xl">
            <div className="flex items-center gap-1.5 mb-3">
              <MapPin size={16} className="text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Last Login</h2>
            </div>
            
            <div className="space-y-1.5">
              <div className="text-sm text-foreground">
                {userData.lastLoginLocation.city}, {userData.lastLoginLocation.region}
              </div>
              <div className="text-xs text-muted-foreground">
                IP: {userData.lastLoginLocation.ipAddress}
              </div>
            </div>
          </div>
        )} */}
      </div>
    </>
  );
};

// Helper component
const InfoRow = ({ label, value, icon: Icon }) => (
  <div>
    <div className="text-xs text-muted-foreground mb-0.5 flex items-center gap-0.5">
      {Icon && <Icon size={12} />}
      {label}
    </div>
    <div className="text-sm text-foreground font-medium truncate">
      {value || 'N/A'}
    </div>
  </div>
);

export default Profile;